import { supabase } from '@/integrations/supabase/client';
import { sendMessageToModel } from '@/services/ModelService';
import type { AIConfig, ModelResponse } from '@/services/ModelService';
import type { AIModel, Message } from '@/types';
import type { MapleMythosExecutionMode, MapleMythosPhaseResult, MapleMythosUsageSnapshot } from './types';

const BILLABLE_PROVIDERS = new Set(['openai', 'gemini', 'deepseek', 'cohere', 'maplept']);

async function getFunctionHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : undefined;
}

export function getMapleMythosExecutionMode(model: AIModel): MapleMythosExecutionMode {
  if (model.provider === 'local') {
    return 'webgpu';
  }

  if (model.provider === 'transformers' || (model.provider === 'ollama' && model.source === 'local')) {
    return 'local';
  }

  return 'cloud';
}

export function isBillableMapleMythosModel(model: AIModel) {
  return getMapleMythosExecutionMode(model) === 'cloud' && model.tier !== 'free' && BILLABLE_PROVIDERS.has(model.provider);
}

export async function getAICreditSnapshot(): Promise<MapleMythosUsageSnapshot | null> {
  try {
    const headers = await getFunctionHeaders();
    const { data, error } = await supabase.functions.invoke('get-ai-credit-account', {
      headers,
    });

    if (error || !data) {
      return null;
    }

    return {
      availableCredits: Number(data.availableCredits || 0),
      canAutoTopup: Boolean(data.canAutoTopup),
      exceeded: Boolean(data.exceeded),
      low: Boolean(data.low),
    };
  } catch {
    return null;
  }
}

export async function runMeteredMapleMythosCall(params: {
  messages: Message[];
  model: AIModel;
  appModels: AIModel[];
  config?: AIConfig;
}): Promise<MapleMythosPhaseResult & { response: ModelResponse }> {
  const { messages, model, appModels, config } = params;
  const shouldMeterCredits = isBillableMapleMythosModel(model);
  const before = shouldMeterCredits ? await getAICreditSnapshot() : null;
  const response = await sendMessageToModel(messages, model, appModels, config);
  const after = shouldMeterCredits ? await getAICreditSnapshot() : null;

  const creditsUsed = before && after
    ? Math.max(0, Number(before.availableCredits || 0) - Number(after.availableCredits || 0))
    : 0;

  return {
    response,
    content: response.content,
    tokensUsed: Number(response.tokensUsed || 0),
    creditsUsed,
    creditSnapshot: after || before || undefined,
  };
}
